import React, { useMemo } from 'react';
import useSWR from 'swr';
import { PageSection } from '../../../types';
import { db } from '../../../services/db';
import { DEFAULT_SHOP_CONTENT } from '../../../services/db/shopContent';
import { MediaTicker, ReviewTicker, mediaForSection, reviewsForSection } from '../coffeenova';

const DEFAULT_TITLES = { REVIEWS: 'Vad kunderna säger', MEDIA: 'Från våra kunder' } as const;

/**
 * Blocken Omdömen och Bildrulle (Studio), på startsidan och i butiken. Samma
 * rullande rader som på produktsidan, med innehållet från Produkter ›
 * Butikens innehåll. Utan något att visa syns blocket inte; i Studio står
 * en förklaring i stället, så att blocket går att hitta och redigera.
 */
export const ShopContentSection: React.FC<{ section: PageSection; editing?: boolean }> = ({ section, editing = false }) => {
    const { data: content = DEFAULT_SHOP_CONTENT } = useSWR('shop-content', () => db.getShopContent(), { revalidateOnFocus: false });
    const type = section.type === 'MEDIA' ? 'MEDIA' : 'REVIEWS';
    const title = section.title?.trim() || DEFAULT_TITLES[type];
    const featuredOnly = !!section.featuredOnly;

    const reviews = useMemo(() => reviewsForSection(content, featuredOnly), [content, featuredOnly]);
    // Bildrullen visar alla kundbilder; "bara utvalda" gäller omdömena.
    const media = useMemo(() => mediaForSection(content), [content]);

    const empty = type === 'REVIEWS'
        ? !reviews.some(r => r.text && r.text.trim())
        : media.reviews.every(r => !r.media || r.media.length === 0) && media.extra.length === 0;

    if (empty) {
        if (!editing) return null;
        return (
            <div className="max-w-3xl mx-auto px-6 py-10 text-center text-sm text-stone-500">
                <p className="font-bold text-stone-700">{title}</p>
                <p className="mt-1">
                    {type === 'REVIEWS'
                        ? featuredOnly ? 'Inga utvalda omdömen än.' : 'Inga publicerade omdömen än.'
                        : 'Inga kundbilder än.'}
                    {' '}Lägg till under Produkter › Butikens innehåll. Blocket syns inte för besökarna förrän det finns något att visa.
                </p>
            </div>
        );
    }

    return (
        <div className="cn-page pb-10 md:pb-14">
            {type === 'REVIEWS'
                ? <ReviewTicker reviews={reviews} title={title} />
                : <MediaTicker reviews={media.reviews} extra={media.extra} title={title} />}
        </div>
    );
};
